import { useQuery, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { showResult } from 'infrastructure/hooks/utils';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';

import type {
  ConversationType,
  IUnreadConversationsCount,
} from 'infrastructure/interfaces';

export const getSmsDashboardUnreadCountFn = async () => {
  const url = `core/conversations/unread-conversations-count`;

  const { data, error } = await dataService.getList<IUnreadConversationsCount>(
    apiUrlGenerator(url),
  );

  if (error) {
    showResult('Unable to retrieve unread conversations count');
    throw new Error(error);
  }
  return data;
};

export const useSmsDashboardUnreadCountKey = () => [
  'getSmsDashboardUnreadCount',
];

const conversationTypeToPropertyName: {
  [key in ConversationType]: keyof IUnreadConversationsCount;
} = {
  rpm: 'rpmTotalUnreadCount',
  ccm: 'ccmTotalUnreadCount',
  unregistered_rpm: 'unregisteredRpmTotalUnreadCount',
  unregistered_ccm: 'unregisteredCcmTotalUnreadCount',
};

interface IProps {
  canLoad?: boolean;
}

export const useSmsDashboardUnreadCount = (props?: IProps) => {
  const { canLoad = true } = props ?? {};
  const queryClient = useQueryClient();

  const {
    data: unreadConversationsCount,
    refetch,
    isFetching,
    isLoading,
    isRefetching,
    ...other
  } = useQuery({
    queryKey: useSmsDashboardUnreadCountKey(),
    queryFn: async () => getSmsDashboardUnreadCountFn(),
    initialData: {
      rpmTotalUnreadCount: 0,
      ccmTotalUnreadCount: 0,
      unregisteredRpmTotalUnreadCount: 0,
      unregisteredCcmTotalUnreadCount: 0,
    },
    enabled: canLoad,
  });

  const updateUnreadCountByType = (type: ConversationType, delta: number) => {
    const key = useSmsDashboardUnreadCountKey();
    queryClient.setQueryData<IUnreadConversationsCount>(key, (oldData) => {
      if (!oldData) return;
      const propertyName = conversationTypeToPropertyName[type];

      if (!propertyName) {
        throw new Error(`Invalid conversation type: ${type}`);
      }
      const current = oldData[propertyName] || 0;
      return {
        ...oldData,
        [propertyName]: current + delta,
      };
    });
  };

  const updateUnreadCount = (data: IUnreadConversationsCount) => {
    const key = useSmsDashboardUnreadCountKey();
    queryClient.setQueryData<IUnreadConversationsCount>(key, (oldData) => {
      if (!oldData) return;

      return {
        rpmTotalUnreadCount: data.rpmTotalUnreadCount,
        ccmTotalUnreadCount: data.ccmTotalUnreadCount,
        unregisteredRpmTotalUnreadCount: data.unregisteredRpmTotalUnreadCount,
        unregisteredCcmTotalUnreadCount: data.unregisteredCcmTotalUnreadCount,
      };
    });
  };

  return {
    unreadConversationsCount: unreadConversationsCount!,
    updateUnreadCountByType,
    updateUnreadCount,
    fetchUnreadConversationsCount: refetch,
    unreadConversationsCountLoading: isLoading || isFetching || isRefetching,
    isLoading,
    isFetching,
    ...other,
  };
};
